import { useTranslation } from "react-i18next"
import i18next from "i18next"
import React from "react"
import "./Timeline.css"
import DateRangePicker from "../FromConnect/DateRangePicker"
import TypePicker from "../FromConnect/TypePicker"
import { TimelineCard } from "./TimelineCard"
import TimelineItemsSkeleton from "./TimelineItemsSkeleton"
import { Activity } from "../../types"
import { useQuery } from "@apollo/client"
import { GET_ACTIVITIES } from "../../api/QuadriApi"
import { useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from "recoil"
import {
  projectIdState,
  projectOriginState,
  quadriProjectIdState,
  sidebarTimelineState,
  usersByTidUuidState,
  needsLicenseState,
  signedInUserState
} from "../StateSelectors"
import NoData from "../NoData/NoData"
import { ConnectUser } from "../../api/ConnectApi"
import { getFilterKey, getOnFilterChanged, loadFilter, saveFilter } from "../../util/filtering"
import { TimelineError } from "./TimelineError"
import MissingLicense from "../MissingLicense"
import ProjectFeatureCatalogLink from "../ProjectFeatureCatalogLink"
import GroupPicker from "../FromConnect/GroupPicker"
import FilterIcon from "../FilterIcon"
import FilterSidebar from "../FilterSidebar"
import UserPicker from "../FromConnect/UserPicker"
import InfiniteScroll from "react-infinite-scroll-component"
import "react-loading-skeleton/dist/skeleton.css"

type ActivityType = "Unknown" | "Receive" | "Release" | "Reserve" | "Share" | "Join"

const TIMELINE_FILTER_STORAGE_KEY = "timelineFilter"
export const scrollable_div = "scrollable_div"

export interface ActivityFilter {
  types: ActivityType[]
  tidUserIds: string[]
  connectUserIds: string[]
  startDate?: string
  endDate?: string
  groupIds: string[]
  groupUserIds: string[]
}

const TimelineMain = ({ activitiesLimit }: { activitiesLimit: number }) => {
  const connectProjectId = useRecoilValue(projectIdState)
  const quadriProjectId = useRecoilValue(quadriProjectIdState)
  const projectOrigin = useRecoilValue(projectOriginState)
  const needsLicense = useRecoilValue(needsLicenseState)
  const signedInUser = useRecoilValue(signedInUserState)
  const setSelectedTimeline = useSetRecoilState(sidebarTimelineState)
  const users = useRecoilValueLoadable(usersByTidUuidState)
  const { t } = useTranslation(["timeline", "common"])

  if (!connectProjectId) throw new Error(t("main.errConnectId)"))
  if (!quadriProjectId) throw new Error(t("main.errQuadriId"))

  const filterKey = getFilterKey(TIMELINE_FILTER_STORAGE_KEY, connectProjectId!)
  console.log("filterkey: ", filterKey)

  const [active, setActive] = React.useState(undefined as undefined | Activity)
  const [filter, setFilter] = React.useState(loadFilter(filterKey, getDefaultFilter()) as ActivityFilter)
  const [activities, setActivities] = React.useState<Activity[]>([])
  const [sfetchedDataSize, setsFetchedDataSize] = React.useState<number>()
  const [fetchError, setFetchError] = React.useState()
  const transData = setupActivityComponentTranslations()
  const typeList = transData.typeList
  const translationSource = transData.translationSource
  console.log("Getting activities, filter: ", filter)

  const { loading, error, data, fetchMore } = useQuery(GET_ACTIVITIES, {
    variables: {
      quadriProjectId,
      filter: {
        ...filter,
        numberOfEntries: activitiesLimit,
        // Need to remove these from the query, or it will result in a 400
        connectUserIds: undefined
        // groupIds: undefined
      },
      offset: 0
    }
  })
  React.useEffect(() => {
    if (data) {
      const activities_retrieved = (data?.getActivities as Activity[]) || []
      setActivities(activities_retrieved)
    }
    saveFilter(filterKey, filter)
  }, [data, filter, filterKey])

  const nActivitiesCurrent = activities.length

  // if initial data fetch is less than chunk_size, then hasMoreData is false
  // if initial data fetch is eq to chunk_size, then check length of subsequent fetches, if less than chunk_size, then hasMoreData is false if eq to chunk_size then true
  let hasMoreData
  if (nActivitiesCurrent < activitiesLimit) {
    hasMoreData = false
  } else if (sfetchedDataSize) {
    hasMoreData = sfetchedDataSize === activitiesLimit ? true : false
  } else {
    hasMoreData = true
  }
  // Followup queries
  const LoadMoreData = async () => {
    console.log("Fetching more data")
    fetchMore({
      variables: {
        quadriProjectId,
        filter: {
          ...filter,
          numberOfEntries: activitiesLimit,
          // Need to remove these from the query, or it will result in a 400
          connectUserIds: undefined
        },
        offset: nActivitiesCurrent
      },
      updateQuery(previousQueryResult, { fetchMoreResult }) {
        console.log("Updating query, with new params")
        const previousData = previousQueryResult?.getActivities
        const newActivities = fetchMoreResult?.getActivities
        if (newActivities) {
          setsFetchedDataSize(newActivities.length)
          const newData = [...previousData, ...newActivities]
          return { getActivities: newData }
        }
      }
    }).catch((e) => {
      console.error(e)
      setFetchError(e)
    })
  }

  const isloading = loading || users.state === "loading"
  const hasError = error || users.state === "hasError" || fetchError

  const onFilterChange = getOnFilterChanged(setFilter)
  const resetFilter = () => {
    setsFetchedDataSize(undefined)
    setFilter(getDefaultFilter())
  }

  const onTimelineCardSelect = (activity: Activity) => {
    setActive(activity)
    setSelectedTimeline({ activity, users: users.contents, quadriProjectId })
  }

  const usersApiUrl = `${projectOrigin}/projects/${connectProjectId}/users?includesRemoved=false`
  const groupsApiUrl = `${projectOrigin}/groups?projectId=${connectProjectId}`
  // const usersApiUrl = `https://app21.connect.trimble.com/tc/api/2.0/projects/KpWXEFjl4iQ/users?includesRemoved=false`

  return needsLicense ? (
    <MissingLicense
      heading={t("licenseErrorMessage.heading", { ns: "common" })}
      body1={t("licenseErrorMessage.body1", { ns: "common" })}
      body2={t("licenseErrorMessage.body2", { ns: "common" })}
      moreInfo={t("forMoreInfo.moreInfo", { ns: "common" })}
      quadriPlans={t("forMoreInfo.quadriPlans", { ns: "common" })}
      info={signedInUser}
      andCheck={t("forMoreInfo.andCheck", { ns: "common" })}
      subscriptions={t("forMoreInfo.subscriptions", { ns: "common" })}
    ></MissingLicense>
  ) : hasError ? (
    <TimelineError error={error ? error : fetchError} />
  ) : (
    <div>
      <div className="head-container">
        <div className="page-heading">
          <h1>{t("main.timelineHeading")}</h1>
          <ProjectFeatureCatalogLink />
        </div>
      </div>

      <div className="toolbar-section">
        <Filter {...{ onFilterChange, groupsApiUrl, filter, resetFilter, usersApiUrl, typeList, translationSource }} />
      </div>
      <div className="row">
        <div className="col-12 col-md-12">
          {!isloading ? (
            activities.length > 0 ? (
              <ActivityCards
                activities={activities as Activity[]}
                active={active}
                onTimelineCardSelect={onTimelineCardSelect}
                connectProjectId={connectProjectId}
                users={users.contents}
                filter={filter}
                scrollElementId={scrollable_div}
                LoadMoreData={LoadMoreData}
                hasMoreData={hasMoreData}
              />
            ) : filterIsActive(filter) ? (
              <NoData
                icon="tc-icon-activity"
                heading={t("main.everythingFilteredOutHeading")}
                message={t("main.everythingFilteredOutMessage")}
              />
            ) : (
              <NoData icon="tc-icon-activity" heading={t("main.noTimeLine")} message={t("main.sharedShow")} />
            )
          ) : (
            <TimelineItemsSkeleton count={5} />
          )}
        </div>
      </div>
    </div>
  )
}

const setupActivityComponentTranslations = () => {
  const typeList: { value: ActivityType; translationKeyForValue: string }[] = [
    { value: "Join", translationKeyForValue: "TASKS_JOINED" },
    { value: "Reserve", translationKeyForValue: "TASKS_RESERVED" },
    { value: "Share", translationKeyForValue: "TASKS_SHARED" },
    { value: "Receive", translationKeyForValue: "TASKS_RECEIVED" },
    { value: "Release", translationKeyForValue: "TASKS_RELEASED" }
  ]

  const translationSource: any[] = [
    {
      type: "DICTIONARY",
      value: {
        // files: translate("files"),
        // FILE_ADD: translate("FILE_ADD"),
        TASKS_JOINED: i18next.t("timeline:main.tasksJoined"),
        TASKS_RESERVED: i18next.t("timeline:main.tasksReserved"),
        TASKS_SHARED: i18next.t("timeline:main.tasksShared"),
        TASKS_RECEIVED: i18next.t("timeline:main.tasksReceived"),
        TASKS_RELEASED: i18next.t("timeline:main.tasksReleased"),
        Label: i18next.t("timeline:main.tasksLabel")
      }
    }
  ]
  return { typeList, translationSource }
}

interface filterProps {
  onFilterChange: (event: Event, which: string) => void
  groupsApiUrl: string
  usersApiUrl: string
  filter: ActivityFilter
  resetFilter: () => void
  typeList: {
    value: ActivityType
    translationKeyForValue: string
  }[]
  translationSource: any[]
}

const Filter = (props: filterProps) => {
  const { t } = useTranslation(["timeline", "common"])
  const { onFilterChange, groupsApiUrl, filter, resetFilter, usersApiUrl, typeList, translationSource } = props
  const [filterSidebar, setFilterSidebar] = React.useState(false)
  const handleFilterSidebar = () => {
    setFilterSidebar(!filterSidebar)
  }
  return (
    <>
      <div className="d-none d-lg-flex">
        <div className="filters">
          <div>
            <div className="filter-type">
              <TypePicker
                onChange={(e: any, which: any) => onFilterChange(e, which)}
                id="timeline-type-picker"
                typeList={typeList}
                selectionList={filter.types.map((t) => typeList.find((tl) => tl.value === t))}
                translationSource={translationSource}
              />
            </div>
            <div className="filter-type">
              <UserPicker
                onChange={onFilterChange}
                apiUrl={usersApiUrl}
                id="activity-user-picker"
                selectionList={filter.connectUserIds}
              />
            </div>
            <div className="filter-type">
              <GroupPicker
                onChange={(e: any, which: any) => onFilterChange(e, which)}
                apiUrl={groupsApiUrl}
                selectionList={filter.groupIds}
                id="group-picker"
              />
            </div>
            <div className="filter-type">
              <DateRangePicker
                onChange={(e: any, which: any) => onFilterChange(e, which)}
                id="activity-daterange-picker"
                selection={{
                  from: filter.startDate || null,
                  to: filter.endDate || null
                }}
              />
            </div>
            {filterIsActive(filter) && (
              <button className="button link-primary" id="filter-reset" onClick={resetFilter}>
                {t("reset", { ns: "common" })}
              </button>
            )}
          </div>
        </div>
      </div>
      {/* <div className="filterIcon d-flex flex-row-reverse d-lg-none col"> */}
      <div className="d-flex flex-row-reverse d-lg-none w-100">
        <FilterIcon handleFilterSidebar={handleFilterSidebar}></FilterIcon>
      </div>
      {/* sidebar for filter (sm to md screens) */}
      <FilterSidebar filterSidebar={filterSidebar} handleFilterSidebar={handleFilterSidebar} resetFilter={resetFilter}>
        <TypePicker
          onChange={(e: any, which: any) => onFilterChange(e, which)}
          id="timeline-type-picker"
          typeList={typeList}
          selectionList={filter.types.map((t) => typeList.find((tl) => tl.value === t))}
          translationSource={translationSource}
        />
        <UserPicker
          id="activity-user-picker"
          onChange={onFilterChange}
          apiUrl={usersApiUrl}
          selectionList={filter.connectUserIds || []}
        />
        <GroupPicker
          onChange={(e: any, which: any) => onFilterChange(e, which)}
          apiUrl={groupsApiUrl}
          selectionList={filter.groupIds}
          id="group-picker"
        />
        <DateRangePicker
          onChange={onFilterChange}
          id="tasks-date-range-picker"
          selection={{
            from: filter.startDate || null,
            to: filter.endDate || null
          }}
        />
      </FilterSidebar>
    </>
  )
}

const ActivityCards = (props: {
  activities: Activity[]
  active: undefined | Activity
  onTimelineCardSelect: (activity: Activity) => void
  connectProjectId: string
  users: { [key: string]: ConnectUser }
  filter: ActivityFilter
  scrollElementId?: string
  LoadMoreData: () => void
  hasMoreData: boolean
}) => {
  const { activities, active, onTimelineCardSelect, connectProjectId } = props
  if (activities.length > 0) {
    return (
      <>
        <InfiniteScroll
          dataLength={props.activities.length}
          next={props.LoadMoreData}
          hasMore={props.hasMoreData}
          loader={<TimelineItemsSkeleton count={3} />}
          scrollableTarget={props.scrollElementId}
        >
          {activities.map((activity, i) => (
            <TimelineCard
              key={`timeline-card-${i}`}
              activity={activity}
              active={active}
              onSelect={onTimelineCardSelect}
              connectProjectId={connectProjectId}
              users={props.users}
            />
          ))}
        </InfiniteScroll>
      </>
    )
  } else {
    return <>No activity yet</>
  }
}

const getDefaultFilter = () => ({
  types: [],
  tidUserIds: [],
  connectUserIds: [],
  groupIds: [],
  groupUserIds: []
})

const filterIsActive = (filter: ActivityFilter): boolean =>
  (filter.types && filter.types.length > 0) ||
  (filter.tidUserIds && filter.tidUserIds.length > 0) ||
  (filter.groupIds && filter.groupIds.length > 0) ||
  (filter.groupUserIds && filter.groupUserIds.length > 0) ||
  typeof filter.startDate !== "undefined" ||
  typeof filter.endDate !== "undefined"

export default TimelineMain
